import React, {memo, useEffect, useState} from "react";
import styles from "./styles.module.scss";
import LazyAssets from "@/app/utils/assets_widget";
import IconButton from "@/app/components/icon_button";
import classNames from "classnames";
import Divider from "@/app/components/divider";
import {s} from "@/app/utils/s";
import {t} from "i18next";
import Button from "@/app/components/button";
import {useLocation, useNavigate} from "react-router-dom";
import {route} from "@/app/routes/AppRoutes";
import {TextMedium12} from "@/app/components/texts";
import {useCookies, CookiesKey} from "@/app/utils/cookies";
import {UserRequests} from "@/app/api/user";
import {ChatRequests} from "@/app/api/chat";
import {useSelector, useDispatch} from "react-redux";
import {RootState, AppDispatch} from "@/app/store";
import {setChatUnreadCount} from "@/app/features/unread_count/slice";

const AppBar = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const {pathname} = useLocation();
  const [url, setUrl] = useState("");
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const {getCookie} = useCookies();
  const dispatch = useDispatch<AppDispatch>();
  
  // Get unread chat count from Redux state
  const unreadChatCount = useSelector((state: RootState) => state.unreadCount.chatCount);
  const shouldShowChatPlusPrefix = useSelector((state: RootState) => state.unreadCount.shouldShowChatPlusPrefix);

  useEffect(() => {
    setUrl(pathname);
  }, [pathname]);

  useEffect(() => {
    if (openMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openMenu]);

  // Helper function to parse notification timestamp with proper timezone handling
  const parseNotificationTimestamp = (timestamp: string | Date) => {
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }

    const timestampStr = timestamp.toString();

    // Check if timestamp already has timezone information
    // Look for timezone patterns: Z, +HH:MM, -HH:MM, +HHMM, -HHMM
    const timezonePattern = /[+-]\d{2}:?\d{2}$|Z$/;
    const hasTimezone = timezonePattern.test(timestampStr);

    if (!hasTimezone) {
      // If no timezone info, assume it's UTC (server time)
      // Add 'Z' to indicate UTC
      const utcTimestamp = timestampStr + 'Z';
      return new Date(utcTimestamp).getTime();
    }

    // If timezone info exists, parse normally
    return new Date(timestampStr).getTime();
  };

  // Fetch notifications and calculate unread count
  const fetchNotifications = async () => {
    try {
      const rawNotifications = await UserRequests.getUserNotifications();

      // Sort notifications by timestamp (newest first) to ensure consistency
      const notifications = rawNotifications.sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0;
        const timeA = parseNotificationTimestamp(a.timestamp);
        const timeB = parseNotificationTimestamp(b.timestamp);
        return timeB - timeA; // Most recent first
      });

      const lastVisitTimestamp = getCookie(CookiesKey.lastNotificationVisit);

      if (lastVisitTimestamp) {
        const lastVisit = parseInt(lastVisitTimestamp);
        const unreadCount = notifications.filter(notification => {
          if (!notification.timestamp) return false;
          const notificationTime = parseNotificationTimestamp(notification.timestamp);

          // Debug logging for timestamp comparison
          if (process.env.NODE_ENV === 'development') {
            // console.log(`Notification timestamp: ${notification.timestamp} -> ${notificationTime} (${new Date(notificationTime).toISOString()})`);
            // console.log(`Last visit: ${lastVisit} (${new Date(lastVisit).toISOString()})`);
            // console.log(`Is unread: ${notificationTime > lastVisit}`);
          }

          return notificationTime > lastVisit;
        }).length;

        setUnreadNotificationCount(unreadCount);
      } else {
        // If no last visit timestamp (e.g., after authorization when cookies are cleared),
        // consider all notifications as read and show count as 0
        setUnreadNotificationCount(0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setUnreadNotificationCount(0);
    }
  };

  // Fetch chats and calculate unread count
  const fetchChatUnreadCount = async () => {
    try {
      const chats = await ChatRequests.getChats(1); // Get first page (10 chats)
      
      // Sum up unreadMessageCount from all chats
      const totalUnreadCount = chats.reduce((sum, chat) => sum + chat.unreadMessageCount, 0);
      
      // Check if all 10 chats have non-zero unreadMessageCount
      const allChatsHaveUnread = chats.length === 10 && chats.every(chat => chat.unreadMessageCount > 0);
      
      // Dispatch Redux action to update shared state
      dispatch(setChatUnreadCount({
        count: totalUnreadCount,
        shouldShowPlusPrefix: allChatsHaveUnread
      }));
    } catch (error) {
      console.error('Error fetching chat unread count:', error);
      // Dispatch Redux action to reset state on error
      dispatch(setChatUnreadCount({
        count: 0,
        shouldShowPlusPrefix: false
      }));
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchNotifications();
    fetchChatUnreadCount();
  }, []);

  // Periodic polling for new notifications and chats (every 30 seconds) - scheduled requests
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications();
      fetchChatUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Refresh counter when user visits notifications page
  useEffect(() => {
    if (pathname === route.notifications) {
      // Small delay to ensure the notifications page has saved the timestamp to cookies
      const timer = setTimeout(() => {
        fetchNotifications();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pathname]);

  // Refresh chat counter when user visits chats page
  useEffect(() => {
    if (pathname === route.chats) {
      // Small delay to ensure the chats page has updated
      const timer = setTimeout(() => {
        fetchChatUnreadCount();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pathname]);

  const navigate = useNavigate();
  return (
    <div className={styles.appBar}>
      <div className={classNames(styles.links, styles.desktop)}>
        <Button text={t(s.home)} icon={<LazyAssets.Home/>} onClick={() => {
          navigate(route.home);
          setOpenMenu(false);
        }} isGreen notExpanded isTransparent={url !== "/"}/>
        <Button text={t(s.map)} icon={<LazyAssets.MapIcon/>} onClick={() => {
          navigate(route.maps);
          setOpenMenu(false);
        }} isGreen notExpanded isTransparent={!url.includes(route.maps)}/>
        <div style={{position: 'relative'}}>
          <Button text={t(s.chat)} icon={<LazyAssets.Chat/>} onClick={() => {
            navigate(route.chats);
            setOpenMenu(false);
          }} isGreen notExpanded isTransparent={!url.includes(route.chats)}/>
          {unreadChatCount !== 0 && <div className={styles.countWrapper}>
              <div className={classNames(styles.count, {[styles.active]: true})} onClick={() => {
                navigate(route.chats);
                setOpenMenu(false);
              }}>
                  <TextMedium12 text={shouldShowChatPlusPrefix ? `+${unreadChatCount}` : unreadChatCount.toString()}/>
              </div>
          </div>}
        </div>
      </div>
      <LazyAssets.LogoPink className={styles.logo} onClick={() => navigate(route.home)}/>
      <div className={classNames(styles.joinTheWaitlist, styles.desktop)}>
        <Button icon={<LazyAssets.Invitation/>} onClick={() => {
          navigate(route.matches);
          setOpenMenu(false);
        }} isGreen circle isTransparent={!url.includes(route.matches)}/>
        <div style={{position: 'relative'}}>
          <Button icon={<LazyAssets.Notification/>} onClick={() => {
            navigate(route.notifications);
            setOpenMenu(false);
          }} isGreen circle isTransparent={!url.includes(route.notifications)}/>
          {unreadNotificationCount !== 0 && <div className={styles.countWrapper}>
              <div className={classNames(styles.count, {[styles.active]: true})} onClick={() => {
                navigate(route.notifications);
                setOpenMenu(false);
              }}>
                  <TextMedium12 text={unreadNotificationCount.toString()}/>
              </div>
          </div>}
        </div>
        <Button icon={<LazyAssets.Profile/>} onClick={() => {
          navigate(route.profile);
          setOpenMenu(false);
        }} isGreen circle isTransparent={!url.includes(route.profile)}/>
        <Button text={t(s.findPlayers)} onClick={() => {
          navigate(route.findPlayers);
          setOpenMenu(false);
        }} isGreen notExpanded/>
      </div>
      <IconButton icon={<LazyAssets.Burger/>} onClick={() => setOpenMenu(true)} className={styles.burgerMenu}/>
      <div className={classNames(styles.blend, {[styles.show]: openMenu})}/>
      <div className={classNames(styles.menu, {[styles.show]: openMenu})}>
        <div className={styles.header}>
          <LazyAssets.Logo className={styles.logo} onClick={() => {
            navigate(route.home);
            setOpenMenu(false);
          }}/>
          <IconButton icon={<LazyAssets.Close className={styles.closeIcon}/>}
                      onClick={() => setOpenMenu(false)}/>
        </div>
        <Divider/>
        <div className={classNames(styles.links, styles.mobile)}>
          <Button text={t(s.home)} icon={<LazyAssets.Home/>} onClick={() => {
            navigate(route.home);
            setOpenMenu(false);
          }} isGreen isTransparent={url !== "/"} alignLeft smallRadius/>
          <Button text={t(s.map)} icon={<LazyAssets.MapIcon/>} onClick={() => {
            navigate(route.maps);
            setOpenMenu(false);
          }} isGreen isTransparent={!url.includes(route.maps)} alignLeft smallRadius/>
          <div style={{position: 'relative'}}>
            <Button text={t(s.chat)} icon={<LazyAssets.Chat/>} onClick={() => {
              navigate(route.chats);
              setOpenMenu(false);
            }} isGreen isTransparent={!url.includes(route.chats)} alignLeft smallRadius/>
            {unreadChatCount !== 0 && <div className={styles.countWrapper}>
                <div className={classNames(styles.count, {[styles.active]: true})}>
                    <TextMedium12 text={shouldShowChatPlusPrefix ? `+${unreadChatCount}` : unreadChatCount.toString()}/>
                </div>
            </div>}
          </div>
          <div style={{position: 'relative'}}>
            <Button text={t(s.notifications)} icon={<LazyAssets.Notification/>} onClick={() => {
              navigate(route.notifications);
              setOpenMenu(false);
            }} isGreen isTransparent={!url.includes(route.notifications)} alignLeft smallRadius/>
            {unreadNotificationCount !== 0 && <div className={styles.countWrapper}>
                <div className={classNames(styles.count, {[styles.active]: true})}>
                    <TextMedium12 text={unreadNotificationCount.toString()}/>
                </div>
            </div>}
          </div>
          <Button text={t(s.matches)} icon={<LazyAssets.Invitation/>} onClick={() => {
            navigate(route.matches);
            setOpenMenu(false);
          }} isGreen isTransparent={!url.includes(route.matches)} alignLeft smallRadius/>
          <Button text={t(s.profile)} icon={<LazyAssets.Profile/>} onClick={() => {
            navigate(route.profile);
            setOpenMenu(false);
          }} isGreen isTransparent={!url.includes(route.profile)} alignLeft smallRadius/>
        </div>
      </div>
    </div>
  );
};

export default memo(AppBar);